import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { OffersService } from '../../../services/offers.service';
import { MatPaginator, MatDialog } from '@angular/material';
import { OfferIface } from '../../../interfaces/offer.iface';
import { isEqual, differenceWith, uniqWith, sortBy } from 'lodash';
import { Status } from '../../../interfaces/status';
import { StatusService } from '../../../services/status.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { Product } from '../../../interfaces/product';
import { ProductsService } from '../../../services/products.service';
import { ProductSelectDialogComponent } from '../offer-edit/product-select-dialog/product-select-dialog.component';
import { OfferFeature } from '../../../interfaces/offerFeature';
import { Tag } from '../../../interfaces/tag';
import { TagCategoriesEnum } from '../../../helpers/tag-categories.enum';
import { DiscountSelectDialogComponent } from '../offer-edit/discount-select-dialog/discount-select-dialog.component';
import { Discount } from '../../../interfaces/discount';
import { ToastsManager } from 'ng2-toastr';
import { SwapsComponent } from './swaps/swaps.component';
import { RESTDataSource } from '../../../helpers/rest.datasource';
import { tap } from 'rxjs/operators';
import { OffersMassEditionService } from '../../../services/offers-mass-edition.service';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { EditTagsDialogComponent } from './edit-tags-dialog/edit-tags-dialog.component';
import { LoggerService } from 'app/services/logger.service';
import { RemoveOfferDialogComponent } from './remove-offer-dialog/remove-offer-dialog.component';

@Component({
  selector: 'app-offer-list',
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.scss']
})
export class CampaignsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(SwapsComponent)
  swaps: SwapsComponent;
  public statuses: Status[];
  public offers: OfferIface[] = [];
  public displayedColumns = [
    'select',
    'edition',
    'delete',
    'name',
    'type',
    'tags',
    'discounts',
    'status',
    'loyaltyPeriod',
    'billingPeriod',
    'activationPrice',
    'linkToTermsOfService',
    'availableSince',
    'availableUntil',
    'availableInLocations',
    'availableInChannels',
    'availableForCustomerSegments',
    'code'
  ];
  public products: Product[];
  public tagCategoriesEnum: object;
  public isLoadingResults = false;
  public iterableTagCategoriesEnum: string[] = [];
  public areAllChangesBeingDisplayed = false;
  private tagCategoriesEnumKeys: string[] = [];
  public dataSource: RESTDataSource<OfferIface>;
  public formFilter: FormControl = new FormControl();
  public filteredOptions: Observable<Tag[]>;
  public subscription: Subscription;
  public tagList: Tag[] = [];

  public static createOffer(offer: OfferIface): OfferIface {
    const { createdAt, createdBy, updatedBy, updatedAt, __v, _id, ...rest } = offer;
    return rest;
  }

  constructor(
    private offersService: OffersService,
    private productsService: ProductsService,
    private statusService: StatusService,
    private dialog: MatDialog,
    private toastr: ToastsManager,
    private offersMassEditionService: OffersMassEditionService
  ) {
    this.dataSource = new RESTDataSource(this.offersService, this.offersMassEditionService);
  }

  ngOnInit() {
    this.subscription = this.dataSource.loading$.subscribe(state => {
      this.isLoadingResults = state;
    });
    this.paginator.pageSize = 10;
    this.swaps.addSwap();
    this.filteredOptions = this.formFilter.valueChanges.pipe(
      startWith(''),
      map(val => this.filter(val))
    );
    this.tagCategoriesEnum = TagCategoriesEnum;
    this.tagCategoriesEnumKeys = Object.keys(this.tagCategoriesEnum);
    for (const prop of this.tagCategoriesEnumKeys) {
      this.iterableTagCategoriesEnum.push(this.tagCategoriesEnum[prop]);
    }
    this.getOffers();
    this.offersService.findTags({}).subscribe(
      tags => {
        this.tagList = tags;
      },
      error => {
        LoggerService.log('CampaignsComponent::ngOnInit::findTags', error);
        this.toastr.error('Problem z pobraniem danych');
      }
    );
    this.statusService.getStatuses().subscribe(
      statuses => {
        this.statuses = statuses;
      },
      error => {
        LoggerService.log('CampaignsComponent::ngOnInit::getStatuses', error);
        this.toastr.error('Problem z pobraniem danych');
      }
    );
    this.productsService.getProducts({}).subscribe(
      products => {
        this.products = products;
      },
      error => {
        LoggerService.log('CampaignsComponent::ngOnInit::getProducts', error);
        this.toastr.error('Problem z pobraniem danych');
      }
    );
  }

  ngAfterViewInit() {
    LoggerService.log('CampaignsComponent::ngAfterViewInit');
    this.paginator.page.pipe(tap(() => this.loadData())).subscribe();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  getOffers(): void {
    this.loadData();
    this.selectAllCheckboxes();
  }

  filter(val: string): Tag[] {
    return this.tagList.filter(option =>
      option.value
        .toLowerCase()
        .trim()
        .includes(val.toLowerCase())
    );
  }

  deleteOffer(id: string): void {
    this.dialog
      .open(RemoveOfferDialogComponent, { disableClose: true })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.offersService.remove(id).subscribe(
            () => {
              this.offers = this.offers.filter(offer => offer._id !== id);
              this.loadData();
              this.selectAllCheckboxes();
              this.toastr.warning('Usunięto ofertę z listy.', 'Alert!');
            },
            error => {
              LoggerService.log('CampaignsComponent::deleteOffer', error);
              this.toastr.error('Problem z usunięciem oferty z listy');
            }
          );
        }
      });
  }

  isAllSelected(): boolean {
    return this.offersMassEditionService.isAllSelected();
  }

  selectAllCheckboxes(): void {
    this.offersMassEditionService.selectAllOffers();
  }

  clearAllCheckboxes(): void {
    this.offersMassEditionService.clearAllOffers();
  }

  select(id: string): void {
    this.offersMassEditionService.selectOffer(id);
  }

  isSelected(id: string): boolean {
    return this.offersMassEditionService.isOfferSelected(id);
  }

  masterToggle(): void {
    this.isAllSelected() ? this.clearAllCheckboxes() : this.selectAllCheckboxes();
  }

  addToggleTag(tag: Tag): void {
    this.paginator.pageIndex = 0;
    this.swaps.toggleTag(tag);
  }

  removeTag(property: string, tag: any, offer: OfferIface): void {
    const idx = offer[property].indexOf(tag);
    if (idx > -1) {
      offer[property].splice(idx, 1);
      this.updateOffer(offer);
    }
  }

  addSwapOption(event): void {
    const name = event.srcElement.innerHTML;
    if (name.includes('"')) {
      return;
    }
    this.swaps.addSwapOption(name);
  }

  changeSingleProperty(value: any, property: string, offer: OfferIface): void {
    if (typeof offer[property] === 'number') {
      try {
        offer[property] = parseFloat(value);
      } catch (err) {
        this.toastr.error('Niepoprawny typ danych');
        return;
      }
    } else {
      offer[property] = value;
    }
    this.updateOffer(offer);
  }

  changeDate(value: any, property: string, offer: OfferIface): void {
    offer[property] = new Date(value).getTime();
    this.updateOffer(offer);
  }

  updateOffer(offer: OfferIface): void {
    this.offersService.update(offer._id, offer).subscribe(
      data => {
        if (data) {
          this.toastr.success('Zmiany zostały zapisane');
        }
      },
      err => {
        this.toastr.error('Niestety', 'Nie udało się zapisać zmian');
      }
    );
  }

  updateTable(): void {
    this.loadData();
    this.selectAllCheckboxes();
  }

  getCellBorderStyle(id: string, prop: string): string {
    if (!this.swaps) {
      return '#FFFFFF';
    }
    const changeInSwap = this.swaps.multipleSwaps.find(
      swap => swap.activeInputs.find(input => input.propertyName === prop) && swap.offersIds.includes(id)
    );
    return changeInSwap ? `5px solid ${changeInSwap.color}` : '#FFFFFF';
  }

  openEditTagsDialog(property: string, offer: OfferIface): void {
    this.dialog
      .open(EditTagsDialogComponent, { data: { property, offer } })
      .afterClosed()
      .subscribe(updatedOffer => {
        if (updatedOffer) {
          this.updateOffer(updatedOffer);
        }
      });
  }

  openProductSelectDialog(offer: OfferIface): void {
    this.dialog
      .open(ProductSelectDialogComponent, { minWidth: '1000px', autoFocus: true })
      .afterClosed()
      .subscribe((offerFeature: OfferFeature) => {
        if (offerFeature) {
          offer.includedServices.push(offerFeature);
          offer.tags.push({ value: offerFeature.product.name, category: offerFeature.product.productCategory });
          this.updateOffer(offer);
        }
      });
  }

  openDiscountSelectDialog(offer: OfferIface): void {
    this.dialog
      .open(DiscountSelectDialogComponent, { minWidth: '1000px', autoFocus: true })
      .afterClosed()
      .subscribe((discount: Discount) => {
        if (discount) {
          offer.discounts.push(discount);
          this.updateOffer(offer);
        }
      });
  }

  openRemoveDialog() {
    const dialogRef = this.dialog.open(RemoveOfferDialogComponent, {
      height: '150px'
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
    });
  }

  removeProductFromOffer(productTag: Tag, offer: OfferIface): void {
    offer.includedServices = offer.includedServices.filter(service => service.product.name !== productTag.value);
    offer.tags = offer.tags.filter(tag => tag.value !== productTag.value);
    this.updateOffer(offer);
  }

  removeDiscountFromOffer(discountTag: Discount, offer: OfferIface): void {
    offer.discounts = offer.discounts.filter(service => service.name !== discountTag.name);
    this.updateOffer(offer);
  }

  showAllChanges(checked: boolean): void {
    this.areAllChangesBeingDisplayed = checked;
    this.paginator.pageIndex = 0;
    this.loadData();
    this.selectAllCheckboxes();
  }

  loadData() {
    if (this.swaps.activeSwap <= -2 || !this.dataSource) {
      return;
    }
    let query = {};
    if (this.areAllChangesBeingDisplayed) {
      query = { _id: { $in: this.offersMassEditionService.modifiedOffersIds } };
    } else if (this.swaps.multipleSwaps.length !== 0) {
      const activeTags = this.swaps.multipleSwaps[this.swaps.activeSwap].activeTags.map(tag => tag.value);
      query = activeTags.length ? { 'tags.value': { $all: activeTags } } : {};
    }
    this.dataSource.loadDataWithModifications(query, this.paginator.pageIndex, this.paginator.pageSize);
  }

  saveChangedOffers(isDuplicate: boolean = false): void {
    const query = { _id: { $in: this.offersMassEditionService.modifiedOffersIds } };
    this.offersService.findAll(query, 0, 0).subscribe(offersToBeModified => {
      const modifiedOffers = this.offersMassEditionService.modifyOffers(offersToBeModified);
      const promises = [];
      modifiedOffers.forEach(offer => {
        if (isDuplicate) {
          const newOffer = CampaignsComponent.createOffer(offer);
          promises.push(this.offersService.create(newOffer));
        } else {
          promises.push(this.offersService.update(offer._id, offer));
        }
      });
      forkJoin(promises).subscribe(
        () => {
          this.getOffers();
          this.swaps.multipleSwaps = [];
          this.swaps.addSwap();
          this.toastr.success('Zapisano zmienione wartości.', 'Sukces!');
        },
        err => {
          console.error(err);
          this.toastr.error('Problem z zapisaniem zmienionych wartości');
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/offers/campaigns/campaigns.component.ts